'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import { BillingForm } from './billing';
import { billingApi, PlanData } from '../services/billingApi';
import logger from '../utils/logger';
// Preload logo images immediately when module loads (using PNG for faster loading)
if (typeof window !== 'undefined') {
    const preloadImage = (src: string) => {
        const img = new window.Image();
        img.src = src;
    };
    preloadImage('/niio_logo_white.png');
    preloadImage('/niio_logo_black.png');
}

interface BillingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    planId: string;
    couponCode?: string;
    accountType?: 'personal' | 'business';
    provider?: 'apple' | 'charge_bee';
    // Called when Google Pay SDK hangs to verify if payment actually succeeded
    onVerifySubscription?: () => Promise<boolean>;
    // Tracking callbacks for A/B test experiment
    onPaymentFailed?: (error: string, paymentMethod?: string) => void;
    onCouponApplied?: (couponCode: string) => void;
    onCouponFailed?: (couponCode: string, error: string) => void;
    onPaymentMethodSelected?: (
        method: 'card' | 'google_pay' | 'apple_pay'
    ) => void;
}

interface PlanDetails {
    displayName: string;
    planType: string; // e.g., 'pro', 'plus', 'basic' - for coupon matching
    baseMonthlyPrice: number;
    annualPrice: number;
    annualDiscountPct: number; // Calculated from actual prices
    currency: string;
    trialDays: number;
    isYearly: boolean;
}

/**
 * Extract plan details from API plan data
 * Calculates discount dynamically from actual monthly/yearly prices
 */
function extractPlanDetails(
    planData: PlanData,
    providerPlanId: string
): PlanDetails {
    const isYearly =
        providerPlanId.toLowerCase().includes('yearly') ||
        providerPlanId.toLowerCase().includes('annual');

    // Find monthly and yearly payment provider plans
    const monthlyPlan = planData.payment_provider_user_plans?.find(
        (p) => p.payment_cycle === 'monthly'
    );
    const yearlyPlan = planData.payment_provider_user_plans?.find(
        (p) => p.payment_cycle === 'annually'
    );

    // Prices from API are already in dollars (server converts from cents)
    const baseMonthlyPrice = monthlyPlan ? monthlyPlan.price : 0;
    const annualPrice = yearlyPlan ? yearlyPlan.price : 0;
    const currency = monthlyPlan?.currency || yearlyPlan?.currency || 'USD';
    const trialDays =
        monthlyPlan?.trial_days ||
        yearlyPlan?.trial_days ||
        planData.trial_days ||
        14;

    // Calculate annual discount percentage dynamically (like AngularJS web app)
    // annualDiscountPct = 100 - (annualMonthlyPrice / baseMonthlyPrice) * 100
    let annualDiscountPct = 0;
    if (baseMonthlyPrice > 0 && annualPrice > 0) {
        const annualMonthlyPrice = annualPrice / 12;
        annualDiscountPct = Math.round(
            100 - (annualMonthlyPrice / baseMonthlyPrice) * 100
        );
    }

    // Plan type for coupon matching (e.g., 'pro', 'plus', 'basic')
    // Use the plan name from user_plans table
    const planType = planData.name?.toLowerCase() || '';

    return {
        displayName: planData.display_name || 'Plan',
        planType,
        baseMonthlyPrice,
        annualPrice,
        annualDiscountPct,
        currency,
        trialDays,
        isYearly,
    };
}

export function BillingModal({
    isOpen,
    onClose,
    onSuccess,
    planId,
    couponCode,
    accountType = 'personal',
    provider = 'charge_bee',
    onVerifySubscription,
    onPaymentFailed,
    onCouponApplied,
    onCouponFailed,
    onPaymentMethodSelected,
}: BillingModalProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [planDetails, setPlanDetails] = useState<PlanDetails | null>(null);
    const [userDetails, setUserDetails] = useState<{
        firstName: string;
        lastName: string;
        email: string;
    } | null>(null);
    // Dark mode toggle for testing (default: dark)
    const [isDarkMode, setIsDarkMode] = useState(true);

    // Load plan details from API and user details from localStorage
    useEffect(() => {
        if (!isOpen) {
            setIsLoading(true);
            setError(null);
            // Clear user details when modal closes to prevent data leakage between sessions
            setUserDetails(null);
            return;
        }

        const loadData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Load user details from localStorage (stored as pairUser by UserContext)
                const storedUser = localStorage.getItem('pairUser');
                if (storedUser) {
                    const parsed = JSON.parse(storedUser);
                    setUserDetails({
                        firstName: parsed.first_name || parsed.firstName || '',
                        lastName: parsed.last_name || parsed.lastName || '',
                        email: parsed.email || '',
                    });
                }

                // Fetch plan data from API
                logger.debug(
                    '[BillingModal] Fetching plan data for:',
                    planId,
                    'provider:',
                    provider
                );
                const planData = await billingApi.getPlanByProviderId(
                    planId,
                    provider
                );

                if (planData) {
                    const details = extractPlanDetails(planData, planId);
                    logger.debug(
                        '[BillingModal] Plan details extracted:',
                        details
                    );
                    setPlanDetails(details);
                } else {
                    console.error('[BillingModal] Plan not found:', planId);
                    setError('Plan not found. Please try again.');
                }
            } catch (e) {
                console.error('[BillingModal] Error loading data:', e);
                setError('Failed to load plan details. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [isOpen, planId, provider]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className={`rounded-2xl w-full max-w-lg max-h-[90vh] relative overflow-hidden shadow-2xl flex flex-col ${
                        isDarkMode
                            ? 'bg-gray-900 border border-gray-800'
                            : 'bg-white'
                    }`}
                >
                    {/* Dark/Light Mode Toggle (for testing) - temporarily hidden */}
                    {/* <button
                        onClick={() => setIsDarkMode(!isDarkMode)}
                        className={`absolute top-4 left-4 z-10 p-2 rounded-lg text-xs font-medium transition-colors ${
                            isDarkMode
                                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        aria-label="Toggle dark mode"
                    >
                        {isDarkMode ? '☀️ Light' : '🌙 Dark'}
                    </button> */}

                    {/* Header - Title only, logo moved to form body */}
                    <div
                        className={`flex-shrink-0 px-4 py-3 flex items-center justify-center relative ${
                            isDarkMode
                                ? 'bg-gray-900 border-b border-gray-800'
                                : 'bg-white border-b border-gray-100'
                        }`}
                    >
                        <h3
                            className={`text-lg font-semibold ${
                                isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}
                        >
                            {planDetails?.trialDays && planDetails.trialDays > 0
                                ? 'Start Your Free Trial'
                                : 'Complete Your Subscription'}
                        </h3>
                        <button
                            onClick={onClose}
                            className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors p-1 ${
                                isDarkMode
                                    ? 'text-gray-500 hover:text-gray-300'
                                    : 'text-gray-400 hover:text-gray-600'
                            }`}
                            aria-label="Close"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                        {/* Loading State */}
                        {isLoading && (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div
                                        className={`w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4 ${
                                            isDarkMode
                                                ? 'border-gray-700 border-t-purple-500'
                                                : 'border-purple-200 border-t-purple-600'
                                        }`}
                                    ></div>
                                    <p
                                        className={
                                            isDarkMode
                                                ? 'text-gray-400'
                                                : 'text-gray-500'
                                        }
                                    >
                                        Loading billing form...
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Error State */}
                        {error && !isLoading && (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center px-6">
                                    <div
                                        className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                                            isDarkMode
                                                ? 'bg-red-900/30'
                                                : 'bg-red-100'
                                        }`}
                                    >
                                        <svg
                                            className={`w-8 h-8 ${
                                                isDarkMode
                                                    ? 'text-red-400'
                                                    : 'text-red-500'
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <p
                                        className={`mb-4 ${
                                            isDarkMode
                                                ? 'text-gray-300'
                                                : 'text-gray-700'
                                        }`}
                                    >
                                        {error}
                                    </p>
                                    <button
                                        onClick={() => {
                                            setError(null);
                                            setIsLoading(true);
                                            setTimeout(
                                                () => setIsLoading(false),
                                                300
                                            );
                                        }}
                                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Native Billing Form */}
                        {!isLoading && !error && planDetails && (
                            <BillingForm
                                planId={planId}
                                planName={planDetails.displayName}
                                userPlanType={planDetails.planType}
                                initialIsYearly={planDetails.isYearly}
                                baseMonthlyPrice={planDetails.baseMonthlyPrice}
                                annualDiscountPct={
                                    planDetails.annualDiscountPct
                                }
                                currency={planDetails.currency}
                                trialDays={planDetails.trialDays}
                                initialCouponCode={couponCode}
                                userFirstName={userDetails?.firstName}
                                userLastName={userDetails?.lastName}
                                userEmail={userDetails?.email}
                                accountType={accountType}
                                onSuccess={onSuccess}
                                onClose={onClose}
                                onVerifySubscription={onVerifySubscription}
                                isDarkMode={isDarkMode}
                                onPaymentFailed={onPaymentFailed}
                                onCouponApplied={onCouponApplied}
                                onCouponFailed={onCouponFailed}
                                onPaymentMethodSelected={
                                    onPaymentMethodSelected
                                }
                            />
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
